import { Column, Row } from '@/components';
import { InputV2 } from '@/components/Input';
import { useService } from '@/containers/ActionServiceV2/context';
import { DEFAULT_AGORA_IMAGE, getSchema } from '@/containers/ActionServiceV2/utils';
import { useClient } from '@/hooks';
import {
  MeetTypeE,
  ServiceI,
  ServiceModalityE,
  SupportedCountriesE,
  WalletTypeE
} from '@/types/cyclone/models';
import { ErrorI, PostServiceI, PostVendorInfoI } from '@/types/cyclone/requests';
import { faLaptop, faPlus, faVideo } from '@fortawesome/free-solid-svg-icons';
import { yupResolver } from '@hookform/resolvers/yup';
import classNames from 'classnames';
import React, { ChangeEvent, FunctionComponent, useMemo, useState } from 'react';
import { Controller, SubmitHandler, useForm } from 'react-hook-form';
import { useMutation } from 'react-query';
import { useNavigate } from 'react-router';
import useLog from '@/hooks/useLog';
import { toast } from 'react-toastify';
import { trackGenericEvent } from '@/analytics';
import { useAuth } from '@/contexts';
import { getCurrencySymbolFromCountry } from '@/utils/money';
import { Switch } from '@headlessui/react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { DownpaymentOption, downPaymentOptions } from '@/containers/ActionServiceV2/context/service';
import { Link } from 'react-router-dom';
import { InputAddress } from '@/components/Input/InputAddress';
import TextArea from '@/components/TextArea/TextArea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/SelectV3';
import { statesOptions } from '@/containers/SelfOnboarding/utils/locations';
import { Building2, Info, User, Users, Video } from 'lucide-react';
import { PaymentMethodSelector } from '@/containers/Services/containers/AdjustmentsServices/containers/AdjustDownpayment/components/Services/PaymentMethodSelector';
import { Button } from '@/components/Button/ButtonV2';
import { Checkbox } from '@/components/Checkbox';
import { SelectV2 } from '@/components/SelectV2';

type FormValues = {
  name: string;
  price: number;
  price_usd?: number;
  duration: number;
  participants: number;
  meetLink?: string;
  address?: string;
  details?: string;
  down_payment_amount?: number;
};

const initialSelectErrors: Record<string, string> = {
  location: '',
  eventDate: '',
  eventName: '',
  price: '',
  duration: '',
  participants: '',
  downPaymentAmount: ''
};

const ControllerWithInput: FunctionComponent<{
  name: string;
  placeholder: string;
  control: any;
  errors?: string;
  type?: 'text' | 'number';
  postfix?: string;
}> = ({ control, errors, name, placeholder, type, postfix }) => {
  return (
    <Controller
      control={control}
      name={name}
      render={({ field: { onChange, value } }) => (
        <InputV2
          id={name}
          placeholder={placeholder}
          onChange={(e) => onChange(e)}
          error={errors ? errors : ''}
          value={value || ''}
          type={type}
          postfix={postfix}
        />
      )}
    />
  );
};

export const ServiceForm: FunctionComponent<{ isSetup: boolean }> = ({ isSetup }) => {
  const { client } = useClient();
  const navigate = useNavigate();
  const { logAndNotify } = useLog();
  const { session, reloadSession } = useAuth();
  const {
    isOnDemand,
    modality,
    isStripeConnected,
    vendorPacks,
    handleSelectPlace,
    selectedPlace,
    label,
    toggleDownPaymentAvailable,
    setToggleDownPaymentAvailable,
    downPaymentType,
    setDownPaymentType,
    downPaymentAvailable,
    setDownPaymentAvailable,
    downPaymentAmount,
    setDownPaymentAmount,
    downPaymentDiscount,
    setDownPaymentDiscount,
    downPaymentAvailableOptions,
    selectedStaffs,
    setSelectedStaffs,
    vendors,
    isOwnAvailability
  } = useService();

  const toggleSelectAllStaff = () => {
    if (selectedStaffs.length === vendors!.length) {
      setSelectedStaffs([]);
    } else {
      setSelectedStaffs(vendors!.map((staff) => staff.id));
    }
  };

  const areAllStaffsSelected = selectedStaffs.length === vendors?.length;

  const country = session ? session.vendor!.country : SupportedCountriesE.ARGENTINA;
  const currency = getCurrencySymbolFromCountry(country);

  const [aboutErrors, setAboutErrors] = useState<Record<string, string>>(initialSelectErrors);
  const [meetType, setMeetType] = useState<MeetTypeE>(MeetTypeE.GOOGLE_MEET);
  const [locationDetails, setLocationDetails] = useState<string>('');
  const [selectedState, setSelectedState] = useState<string>('');

  const isLocal = modality === ServiceModalityE.LOCAL;

  const handleErrors = (): boolean => {
    const { duration, name, meetLink, participants, price } = watch();

    const errors = [
      {
        meetLink: {
          boolean: modality === ServiceModalityE.VIRTUAL && !meetLink,
          message: 'Ingresa el link del encuentro'
        }
      },
      {
        name: {
          boolean: !name || name.length < 2,
          message: 'Ingresa el nombre del servicio (mínimo 2 caracteres)'
        }
      },
      {
        duration: {
          boolean: !duration || duration <= 0,
          message: 'Ingresa la duración del servicio'
        }
      },
      {
        participants: {
          boolean: !isOnDemand && (!participants || participants <= 0),
          message: 'Ingresa la cantidad de participantes'
        }
      },
      {
        address: {
          boolean: modality === ServiceModalityE.LOCAL && !selectedPlace,
          message: 'Ingresa la dirección del servicio'
        }
      },
      {
        price: {
          boolean: !price || price < 0,
          message: 'Ingresa el precio del servicio'
        }
      }
    ];

    const errorsObject = errors.reduce((acc, error) => {
      const key = Object.keys(error)[0];
      const { boolean, message } = Object.values(error)[0];
      return {
        ...acc,
        [key]: boolean ? message : ''
      };
    }, initialSelectErrors);

    setAboutErrors(errorsObject);

    const firstErrorKey = Object.keys(errorsObject).find((key) => errorsObject[key]);
    if (firstErrorKey) {
      const element = document.getElementById(firstErrorKey);
      if (element) {
        element.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
    }

    const hasErrors = Object.values(errorsObject).some((message) => message !== '');

    if (hasErrors) {
      const errorMessage = Object.values(errorsObject)
        .filter((message) => message)
        .join('\r\n');
      logAndNotify(`Error al crear un servicio - ${errorMessage}`);
      toast.error(errorMessage);
      return true;
    } else {
      if (toggleDownPaymentAvailable) {
        if (!downPaymentAmount && !downPaymentDiscount) {
          toast.error('El monto de la seña es requerido');
          logAndNotify(`Error al crear un servicio - El monto de la seña es requerido`);
          return true;
        }

        if (downPaymentAvailable.length === 0) {
          toast.error('Debe haber al menos un medio de pago habilitado para la seña');
          logAndNotify(
            `Error al crear un servicio - Debe haber al menos un medio de pago habilitado para la seña`
          );
          return true;
        }

        if (downPaymentType.value === 'percentage') {
          if (!downPaymentDiscount) {
            toast.error('El porcentaje de la seña es requerido');
            logAndNotify(`Error al crear un servicio - El porcentaje de la seña es requerido`);
            return true;
          }
          if (downPaymentDiscount >= 91) {
            toast.error('El porcentaje de la seña no puede ser mayor a 90%');
            logAndNotify(`Error al crear un servicio - El porcentaje de la seña no puede ser mayor a 90%`);
            return true;
          }
        }

        if (downPaymentType.value === 'amount') {
          if (!downPaymentAmount) {
            toast.error('El monto de la seña es requerido');
            logAndNotify(`Error al crear un servicio - El monto de la seña es requerido`);
            return true;
          }
          if (downPaymentAmount <= 0) {
            toast.error('El monto de la seña no puede ser menor o igual a 0');
            logAndNotify(`Error al crear un servicio - El monto de la seña no puede ser menor o igual a 0`);
            return true;
          }
          if (downPaymentAmount > 0.91 * price) {
            toast.error('La seña no puede superar el 90% del precio total del servicio');
            logAndNotify(
              `Error al crear un servicio - La seña no puede superar el 90% del precio total del servicio`
            );
            return true;
          }
        }
      }
      return false;
    }
  };

  const { handleSubmit, watch, control } = useForm<FormValues>({
    resolver: yupResolver(getSchema(modality))
  });

  React.useLayoutEffect(() => {
    if (session?.vendor?.address) {
      handleSelectPlace({
        address: session.vendor.address,
        address_details: session.vendor.address_details,
        latitude: session.vendor.latitude,
        longitude: session.vendor.longitude,
        neighborhood: session.vendor.neighborhood,
        country: session.vendor.country,
        state: session.vendor.state
      });
    }

    if (session?.vendor?.address_details) {
      setLocationDetails(session.vendor.address_details);
    }
    if (session?.vendor?.state) {
      setSelectedState(session.vendor.state);
    }
  }, []);

  const isFirstService = session?.vendor?.vendor_activity_tracking?.first_service_at === null;

  const mutationInfo = useMutation<Partial<PostVendorInfoI>, ErrorI>(
    (vendor) =>
      client(`me/vendor/info`, 'PUT', {
        isAuthRequired: true,
        data: vendor
      }),
    {
      onError: () => {
        toast.error('Hubo un error al guardar la dirección del servicio');
      }
    }
  );

  const onSubmit: SubmitHandler<FormValues> = async (data) => {
    try {
      const { name, price, participants, duration, meetLink, price_usd } = data;
      const isDownPaymentPercentage = downPaymentType.value === 'percentage';

      const submitData: PostServiceI = {
        name,
        price: price || 0,
        price_usd: price_usd || 0,
        modality: modality!,
        cover_url: DEFAULT_AGORA_IMAGE,
        ...(locationDetails && { address_details: locationDetails }),
        ...(selectedPlace && {
          address: JSON.stringify({
            ...selectedPlace,
            address_details: locationDetails
          })
        }),
        duration,
        participants: isOnDemand ? 1 : participants,
        ...(modality === ServiceModalityE.VIRTUAL && {
          meet_type: meetType,
          meet_link: meetLink
        }),
        packs: JSON.stringify(vendorPacks),
        description: '',
        booking_until_at_minutes: '60',
        policy_refund_mins: 0,
        is_for_subscription: false,
        is_on_demand: participants === 1,
        is_refund_policies_enabled: false,
        is_refund_available: false,
        is_user_phone_required: true,
        vendor_ids: JSON.stringify(selectedStaffs),
        is_down_payment_available: toggleDownPaymentAvailable,
        down_payment_amount: !isDownPaymentPercentage ? (downPaymentAmount as number) : undefined,
        down_payment_discount:
          isDownPaymentPercentage && downPaymentDiscount ? downPaymentDiscount / 100 : undefined,
        ...(label && { label: label }),
        down_payment_methods: JSON.stringify(downPaymentAvailable),
        // @ts-ignore
        has_own_availability: isOwnAvailability
      };

      if (isFirstService) {
        const infoAddress = {
          address: JSON.stringify({
            address: selectedPlace?.address,
            address_details: selectedPlace?.address_details,
            state: selectedPlace?.state,
            country: selectedPlace?.country,
            latitude: selectedPlace?.latitude,
            longitude: selectedPlace?.longitude
          })
        };

        await mutationInfo.mutateAsync(infoAddress as any);
        await mutationCreate.mutateAsync(submitData);
      } else {
        await mutationCreate.mutateAsync(submitData);
      }
    } catch (error) {
      if (error instanceof Error) {
        console.error(`Error al crear el servicio: ${error.message}`);
      } else {
        console.error(`Error desconocido al crear el servicio: ${error}`);
      }
    }
  };

  const fullSubmit = () => {
    trackGenericEvent('Button Save Asset Clicked', { type: 'Service' });

    if (handleErrors()) {
      return;
    }

    handleSubmit(onSubmit, (e) => {
      if (e) {
        const errors = Object.values(e).map((error) => error.message);
        toast.error(errors.join('\r\n'));
        const firstErrorKey = Object.keys(e)[0];
        const element = document.getElementById(firstErrorKey);
        if (element) {
          element.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
      } else {
        window.scrollTo({ top: 0, behavior: 'smooth' });
        logAndNotify(`Error al crear un servicio - error no se puedo identificar check ASAP`);
        toast.error('Error al guardar el servicio');
      }
    })();
  };

  const buildLabel = useMemo(() => {
    const labels: Record<string, string> = {
      [SupportedCountriesE.CHILE]: 'región',
      [SupportedCountriesE.URUGUAY]: 'departamento'
    };

    return country ? labels[country] || 'provincia' : 'provincia';
  }, [country]);

  const mutationCreate = useMutation<ServiceI, ErrorI, PostServiceI>(
    (service) =>
      client('services', 'POST', {
        isAuthRequired: true,
        isMultipartForm: true,
        data: service
      }),
    {
      onSuccess: (service) => {
        if (isFirstService) {
          // Set flag to show loading in FirstServiceStep while session reloads
          localStorage.setItem('cj_service_created_waiting', 'true');
        }
        reloadSession();
        toast.success('Servicio creado');
        if (isFirstService) navigate('/servicios?isComplete=true');
        else navigate(`/servicio/${service.id}/editar`);
      },
      onError: (e: any) => {
        logAndNotify(`Error al crear un servicio - ${JSON.stringify(e)}`);
        toast.error('Algo anda mal. Por favor, contactar a soporte.');
      }
    }
  );

  const shouldShowDownPayment =
    isFirstService &&
    (session?.vendor?.is_payment_bank_transfer_enabled ||
      session?.vendor?.is_payment_cash_enabled ||
      session?.vendor?.payment_gateways?.includes(WalletTypeE.MERCADO_PAGO));

  const getAvailablePMOptions = () => {
    const options = [];
    const isWalletAssociated =
      session?.vendor?.payment_gateways && session.vendor.payment_gateways.length > 0;

    if (isWalletAssociated) {
      options.push(DownpaymentOption.MERCADO_PAGO);
    }
    if (session?.vendor?.is_payment_bank_transfer_enabled) {
      options.push(DownpaymentOption.BANK_TRANSFER);
    }
    if (session?.vendor?.is_payment_cash_enabled) {
      options.push(DownpaymentOption.CASH);
    }
    return options;
  };

  return (
    <div className={classNames('flex flex-col gap-6', { 'px-4 sm:px-0': !isSetup })}>
      {/* Header */}
      <div className={`space-y-1 ${isSetup ? '!self-center' : ''}`}>
        <h2 className="text-2xl font-medium text-gray-900">
          {isSetup ? 'Agrega tu primer servicio' : 'Detalles del servicio'}
        </h2>
        <p className="text-[#626262] text-sm">Completa la información de tu nuevo servicio</p>
      </div>

      {/* Form Container */}
      <div
        className={classNames('w-full', {
          'border-2 border-gray-200 rounded-xl p-6 sm:p-8 bg-white': !isSetup
        })}
      >
        <div className="space-y-8">
          {/* Options Selection  */}
          <div className="flex items-center gap-2 mb-4">
            <div className="flex w-fit items-center gap-3 rounded-xl border border-blue/20 px-4 py-1 bg-blue/10">
              {isOnDemand ? (
                <User className="h-5 w-5 text-blue flex-shrink-0" />
              ) : (
                <Users className="h-5 w-5 text-blue flex-shrink-0" />
              )}
              <span className="text-base text-blue">{isOnDemand ? 'Personalizado' : 'Grupal'}</span>
            </div>
            <div className="flex w-fit items-center gap-3 rounded-xl border border-blue/20 px-4 py-1 bg-blue/10">
              {isLocal ? (
                <Building2 className="h-5 w-5 text-blue flex-shrink-0" />
              ) : (
                <Video className="h-5 w-5 text-blue flex-shrink-0" />
              )}
              <span className="text-base text-blue">{isLocal ? 'Presencial' : 'Online'}</span>
            </div>
          </div>

          {!isOnDemand && (
            <div className="space-y-4">
              <span className="text-xl font-medium text-gray-900">Participantes</span>
              <div className="w-full sm:w-1/2">
                <ControllerWithInput
                  name="participants"
                  placeholder="Participantes (2 o más)"
                  control={control}
                  errors={aboutErrors.participants}
                  type="number"
                />
              </div>
            </div>
          )}

          {/* Service Name */}
          <div className="space-y-4">
            <span className="text-xl font-medium text-gray-900">Nombre del servicio</span>
            <div className="w-full sm:w-[80%]">
              <ControllerWithInput
                name="name"
                placeholder={
                  modality === ServiceModalityE.LOCAL
                    ? session?.is_beauty_category
                      ? 'Ej: Esmaltado semipermanente'
                      : 'Ej: Consulta presencial'
                    : 'Ej: Consulta online'
                }
                control={control}
                errors={aboutErrors.name}
              />
            </div>
          </div>

          {/* Duration */}
          <div className="space-y-4">
            <span className="text-xl font-medium text-gray-900">Duración de cada encuentro</span>
            <div className="w-full sm:w-1/2">
              <ControllerWithInput
                name="duration"
                placeholder="Duración"
                postfix="mins"
                control={control}
                errors={aboutErrors.duration}
                type="number"
              />
            </div>
          </div>

          {/* Modality */}
          <div className="space-y-4">
            {modality === ServiceModalityE.LOCAL ? (
              <>
                <span className="text-xl font-medium text-gray-900">Dirección</span>
                <div className="space-y-4">
                  <div className="space-y-4">
                    {isFirstService && country !== SupportedCountriesE.CHILE && (
                      <div className="w-full sm:w-[80%]">
                        <Select
                          value={selectedState || ''}
                          onValueChange={(value) => setSelectedState(value)}
                        >
                          <SelectTrigger className="w-full !text-base !border-[#0000005c]">
                            <SelectValue placeholder={`Selecciona tu ${buildLabel}`}>
                              {selectedState || `Selecciona tu ${buildLabel}`}
                            </SelectValue>
                          </SelectTrigger>
                          <SelectContent className="!max-h-[200px]">
                            {statesOptions[country as SupportedCountriesE].map((option) => (
                              <SelectItem
                                className={classNames({
                                  'bg-blue-500 text-white': option.value === selectedState,
                                  'hover:bg-gray-100': option.value !== selectedState
                                })}
                                key={option.value}
                                value={option.label as string}
                              >
                                {option.label}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                    )}
                    <div className="w-full sm:w-[80%]">
                      <InputAddress
                        selectedPlace={selectedPlace}
                        onSelect={handleSelectPlace}
                        country={country}
                        state={isFirstService ? selectedState : session?.vendor?.state}
                      />
                    </div>
                    <div className="w-full sm:w-[80%]">
                      <TextArea
                        placeholder="Piso, departamento, otras instrucciones"
                        value={locationDetails || ''}
                        onChange={(value) => setLocationDetails(value)}
                        autoExpand={false}
                        maxLength={70}
                        showCount
                      />
                    </div>
                  </div>
                </div>
              </>
            ) : (
              <>
                <span className="text-xl font-medium text-gray-900">Link de reunión</span>
                <Column gap={20}>
                  <Row
                    align="center"
                    justify="space-between"
                    gap={8}
                    className="w-full md:w-1/2 p-2 text-base font-medium bg-[#F4F4F4]"
                  >
                    <Row
                      align="center"
                      justify="center"
                      gap={8}
                      onClick={() => {
                        setMeetType(MeetTypeE.ZOOM);
                      }}
                      className={classNames({
                        'p-2 w-1/2 cursor-pointer': true,
                        'bg-white rounded shadow-[0px_2px_2px_0px_#00000040] transition-all duration-300':
                          meetType === MeetTypeE.ZOOM
                      })}
                    >
                      <FontAwesomeIcon icon={faVideo} className="text-[#868686]" />
                      <span className="text-base text-[#868686] text-center">Zoom</span>
                    </Row>
                    <Row
                      align="center"
                      justify="center"
                      gap={8}
                      onClick={() => {
                        setMeetType(MeetTypeE.GOOGLE_MEET);
                      }}
                      className={classNames({
                        'p-2 w-1/2 cursor-pointer': true,
                        'bg-white rounded shadow-[0px_2px_2px_0px_#00000040] transition-all duration-300':
                          meetType === MeetTypeE.GOOGLE_MEET
                      })}
                    >
                      <FontAwesomeIcon icon={faLaptop} className="text-[#868686]" />
                      <span className="text-base text-[#868686] text-center">Meet</span>
                    </Row>
                  </Row>
                  <div className="w-full md:w-[80%]">
                    <ControllerWithInput
                      name="meetLink"
                      placeholder="Pega el link acá"
                      control={control}
                      errors={aboutErrors.meetLink}
                    />
                  </div>
                </Column>
              </>
            )}
          </div>

          {/* Price */}
          <div className="space-y-4">
            <div className="space-y-1">
              <span className="text-xl font-medium text-gray-900">Precio total del servicio</span>
              <p className="text-sm text-[#626262]">
                Este es el valor total que pagará tu cliente por el servicio brindado.
              </p>
            </div>
            <div className="flex flex-col sm:flex-row gap-4 w-full sm:w-[80%]">
              <div className={isStripeConnected ? 'w-full sm:w-1/2' : 'w-full'}>
                <ControllerWithInput
                  name="price"
                  placeholder={isSetup ? 'Precio' : country}
                  control={control}
                  errors={aboutErrors.price}
                  type="number"
                  postfix={currency}
                />
              </div>
              {isStripeConnected && (
                <div className="w-full sm:w-1/2">
                  <ControllerWithInput
                    name="price_usd"
                    placeholder="Otro país"
                    control={control}
                    type="number"
                    postfix="US$"
                  />
                </div>
              )}
            </div>
          </div>

          {/* Down Payment */}
          {shouldShowDownPayment && (
            <div className="space-y-6">
              <div className="space-y-2">
                <div className="flex items-center gap-4">
                  <span className="text-xl font-medium text-gray-900">Sólo cobrar seña para reservar</span>
                  <Switch
                    checked={toggleDownPaymentAvailable}
                    onChange={() => {
                      if (!toggleDownPaymentAvailable) {
                        setDownPaymentAvailable(downPaymentAvailableOptions);
                      }
                      setToggleDownPaymentAvailable(!toggleDownPaymentAvailable);
                    }}
                    className={`${
                      toggleDownPaymentAvailable ? 'bg-blue/20' : 'bg-gray-300'
                    } relative inline-flex h-6 w-11 items-center rounded-full transition-colors`}
                  >
                    <span
                      className={`${
                        toggleDownPaymentAvailable ? 'translate-x-6 bg-blue' : 'translate-x-1 bg-white'
                      } inline-block h-4 w-4 transform rounded-full transition-transform`}
                    />
                  </Switch>
                </div>
                <p className="text-sm text-[#626262] sm:w-[80%]">
                  Al ingresar una reserva para este servicio, tus clientes visualizarán la seña que deben
                  pagar para asegurar su turno.
                </p>
              </div>

              {toggleDownPaymentAvailable && (
                <div className="space-y-6">
                  <div className="flex flex-col sm:flex-row gap-4">
                    <div className="w-full sm:w-1/4">
                      <SelectV2
                        selected={downPaymentType}
                        setSelected={setDownPaymentType as any}
                        options={downPaymentOptions}
                      />
                    </div>
                    {downPaymentType.value === 'amount' && (
                      <div className="w-full sm:w-[calc(55%-16px)]">
                        <InputV2
                          id="amount"
                          placeholder="Seña"
                          value={downPaymentAmount ?? ''}
                          onChange={(e: ChangeEvent<HTMLInputElement>) =>
                            setDownPaymentAmount(Number(e.target.value))
                          }
                          postfix={currency}
                          type="number"
                        />
                      </div>
                    )}
                    {downPaymentType.value === 'percentage' && (
                      <div className="w-full sm:w-[calc(55%-16px)]">
                        <InputV2
                          id="discountAmount"
                          placeholder="Seña"
                          value={downPaymentDiscount ?? ''}
                          onChange={(e: ChangeEvent<HTMLInputElement>) =>
                            setDownPaymentDiscount(Number(e.target.value))
                          }
                          postfix="%"
                          type="number"
                        />
                      </div>
                    )}
                  </div>

                  <div className="space-y-3 sm:w-[80%]">
                    <label className="font-medium text-gray-900">Medios de cobro aceptados para señas</label>
                    <PaymentMethodSelector
                      selectedMethods={downPaymentAvailable}
                      onSelectMethod={(value) =>
                        setDownPaymentAvailable(
                          downPaymentAvailable.includes(value)
                            ? downPaymentAvailable.filter((m) => m !== value)
                            : [...downPaymentAvailable, value]
                        )
                      }
                      availableMethods={getAvailablePMOptions()}
                    />
                  </div>

                  {(downPaymentAvailableOptions.includes('cash') ||
                    downPaymentAvailableOptions.includes('bank_transfer')) && (
                    <div className="bg-blue/10 border-2 border-blue/30 rounded-xl p-4 sm:w-[80%] w-full">
                      <div className="flex items-start gap-3">
                        <Info className="h-5 w-5 text-blue mt-0.5 flex-shrink-0" />
                        <div className="space-y-2">
                          <p className="font-medium text-blue">Notas importantes:</p>
                          <ul className="list-disc text-sm text-blue space-y-2">
                            <li>
                              Para pagos en efectivo o por transferencia, deberás gestionar el cobro de la
                              seña con tu cliente.
                            </li>
                            <li>
                              Los descuentos por medio de pago se aplicarán tanto a la seña como al valor
                              total del servicio.
                            </li>
                          </ul>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          )}
          {/* Staff Selection */}
          {session?.is_venue && isOnDemand && (
            <div className="space-y-4">
              <div className="space-y-1">
                <span className="text-xl font-medium text-gray-900">Personal</span>
                <p className="text-sm text-[#626262] sm:w-[80%]">
                  Selecciona los integrantes de tu equipo que prestan este servicio.
                </p>
              </div>
              <div className="w-full">
                {!vendors ? (
                  <Link to={'/equipo/agregar'} target="_blank">
                    <div className="flex items-center justify-center gap-2 w-fit p-3 rounded-xl border-2 border-gray-200 hover:border-blue-300 transition-all duration-200">
                      <FontAwesomeIcon icon={faPlus} className="text-blue-500 h-5 w-5" />
                      <p className="text-base text-blue">Agregar integrantes a mi equipo</p>
                    </div>
                  </Link>
                ) : (
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div
                      onClick={toggleSelectAllStaff}
                      className="flex items-center gap-4 px-4 py-2 border-2 border-gray-200 rounded-xl cursor-pointer hover:border-blue-300 transition-all duration-200"
                    >
                      <Checkbox enable={areAllStaffsSelected} />
                      <p className="text-blue-500">Seleccionar todo el personal</p>
                    </div>
                    {vendors
                      .sort((a, b) =>
                        `${a.user.first_name} ${a.user.last_name}`.localeCompare(
                          `${b.user.first_name} ${b.user.last_name}`
                        )
                      )
                      .map((staff) => {
                        const isSelected = selectedStaffs.includes(staff.id);
                        const handleSelectStaff = () => {
                          if (isSelected) {
                            setSelectedStaffs(selectedStaffs.filter((id) => id !== staff.id));
                          } else {
                            setSelectedStaffs([...selectedStaffs, staff.id]);
                          }
                        };
                        return (
                          <div
                            key={staff.id}
                            onClick={handleSelectStaff}
                            className={`flex items-center gap-4 px-4 py-2 rounded-xl border-2 transition-all duration-200 cursor-pointer ${
                              isSelected ? 'border-blue bg-blue/10' : 'border-gray-200 hover:border-blue-300'
                            }`}
                          >
                            <Checkbox enable={isSelected} />
                            <span className="text-gray-900">
                              {staff.user.first_name} {staff.user.last_name}
                            </span>
                          </div>
                        );
                      })}
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Buttons */}
          <div className="flex flex-col sm:flex-row gap-4 justify-center pt-8">
            {!isFirstService && (
              <Button size="lg" variant="outline" onClick={() => navigate('/servicios')}>
                Cancelar
              </Button>
            )}
            <Button
              size="lg"
              isLoading={mutationCreate.isLoading}
              onClick={fullSubmit}
              className={`${
                isSetup
                  ? 'flex-1 !text-base shadow-md hover:shadow-lg transition-all h-14 sm:h-12 rounded-xl bg-blue hover:bg-blue/90 w-full'
                  : ''
              } `}
            >
              Crear servicio
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};
